import { Container, Section } from '@layout';
import React from 'react';

interface MainHeroProps {
  main?: React.ReactNode;
  card?: React.ReactNode;
  className?: string;
  /** Anchor id for in-page links (e.g. `#design-system-hero`). */
  id?: string;
  /**
   * Section background photo 1× URL (e.g. **`hero.hero_bg`** from JSON via **`readHeroBg`**). Sets **`hero-photo`**
   * and **`--hero-photo-url`**; wash is **`.hero-fg`** + **`--hero-photo-overlay`**.
   */
  hero_bg?: string;
  /** 2× URL (e.g. **`hero.hero_bg_2x`**). Enables **`hero-photo-retina`** + **`image-set`**. */
  hero_bg_2x?: string;
  /** Optional custom layer (e.g. extra markup); only when **`hero_bg`** is not set. Renders inside **`.hero-bg-overlay`**. */
  hero_bg_overlay?: React.ReactNode;
}

const MainHero: React.FC<MainHeroProps> = ({
  main,
  card,
  className = '',
  id,
  hero_bg,
  hero_bg_2x,
  hero_bg_overlay,
}) => {
  const photo1x = hero_bg?.trim();
  const photo2x = photo1x ? hero_bg_2x?.trim() : undefined;
  const showSectionPhoto = Boolean(photo1x);

  const sectionStyle: React.CSSProperties | undefined = showSectionPhoto
    ? {
        ['--hero-photo-url' as string]: `url(${JSON.stringify(photo1x)})`,
        ...(photo2x
          ? { ['--hero-photo-url-2x' as string]: `url(${JSON.stringify(photo2x)})` }
          : {}),
      }
    : undefined;

  const sectionClassName = [
    'hero',
    className.trim() || undefined,
    showSectionPhoto ? 'hero-photo' : undefined,
    showSectionPhoto && photo2x ? 'hero-photo-retina' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const hasCustomOverlay =
    !showSectionPhoto &&
    hero_bg_overlay != null &&
    hero_bg_overlay !== false &&
    !(typeof hero_bg_overlay === 'string' && hero_bg_overlay.trim() === '');
  const showHeroBgOverlay = hasCustomOverlay;

  return (
    <Section sectionID={id} style={sectionStyle} className={sectionClassName}>
      {showSectionPhoto ? <div className='hero-fg' aria-hidden='true' /> : null}
      {showHeroBgOverlay ? (
        <div className='hero-bg-overlay' aria-hidden='true'>
          {hero_bg_overlay}
        </div>
      ) : null}
      <Container className='container hero-inner'>
        <div className={card ? 'hero-grid' : 'hero-full'}>
          {main && <div className='hero-main'>{main}</div>}
          {card && <div className='hero-card'>{card}</div>}
        </div>
      </Container>
    </Section>
  );
};

export default MainHero;
